import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type State = "loading" | "valid" | "already" | "invalid" | "submitting" | "success" | "error";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>("loading");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    (async () => {
      try {
        const r = await fetch(`${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`, {
          headers: { apikey: ANON },
        });
        const j = await r.json();
        if (r.ok && j.valid) setState("valid");
        else if (j.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      } catch { setState("invalid"); }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setState("submitting");
    try {
      const r = await fetch(`${SUPABASE_URL}/functions/v1/handle-email-unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON },
        body: JSON.stringify({ token }),
      });
      const j = await r.json();
      if (r.ok && j.success) setState("success");
      else if (j.reason === "already_unsubscribed") setState("already");
      else setState("error");
    } catch { setState("error"); }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <SEO title="Désabonnement" description="Désabonnement des emails." noindex />
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center shadow-sm">
        <h1 className="text-2xl font-serif text-foreground mb-4">Désabonnement</h1>
        {state === "loading" && <p className="text-muted-foreground">Vérification du lien…</p>}
        {state === "valid" && (
          <>
            <p className="text-muted-foreground mb-6">
              Confirmez votre désabonnement des emails de Cygnature Production.
            </p>
            <Button onClick={confirm} className="w-full">Confirmer le désabonnement</Button>
          </>
        )}
        {state === "submitting" && <p className="text-muted-foreground">Traitement en cours…</p>}
        {state === "success" && (
          <p className="text-foreground">Vous êtes désabonné. Vous ne recevrez plus d'emails de notre part.</p>
        )}
        {state === "already" && (
          <p className="text-foreground">Cette adresse est déjà désabonnée.</p>
        )}
        {state === "invalid" && (
          <p className="text-destructive">Lien invalide ou expiré.</p>
        )}
        {state === "error" && (
          <p className="text-destructive">Une erreur est survenue. Veuillez réessayer plus tard.</p>
        )}
      </div>
    </main>
  );
}
